import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Modal, Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckoutForm from './CheckoutForm';
import CardImage from '../../../images/card.png';
import OrderImage from '../../../images/order.png';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';

const Card = styled('div')`
  border-radius: 8px;
  border: 1px solid #4062E533;
  background: #F7FAFC;
  height: 100%;
`;

const CardHeader = styled('div')`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #4062E533;
`;

const CardBody = styled('div')`
  padding: 16px;
`;

const stripePromise = loadStripe(
  process.env.REACT_APP_NODE_ENV === 'production'
    ? process.env.REACT_APP_STRIPE_PUBLIC_KEY : process.env.REACT_APP_STAGING_STRIPE_PUBLIC_KEY,
);

function PaymentModal({
  closeModal, membership, membersCount, plan, onPaymentSuccess, activeWorkspace,
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState('override');

  // Calculate minimum members: max of plan's Members limit, 5, or current member count
  const minimumMembers = Math.max(plan.Members || 5, membersCount, 5);
  const [editableMembersCount, setEditableMembersCount] = useState(minimumMembers);

  const originalPrice = plan.price * editableMembersCount;
  const finalPrice = discountInfo ? discountInfo.finalPrice : originalPrice;

  // Check for active subscription
  useEffect(() => {
    let isMounted = true;
    async function checkActiveSubscription() {
      try {
        const res = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.getActiveSubscription(activeWorkspace.id),
        });
        console.log('Active subscription check response:', res.data);
        if (isMounted && res.data?.hasActiveSubscription) {
          setHasActiveSubscription(true);
          console.log('Active subscription found, showing mode selection');
        } else {
          console.log('No active subscription found');
        }
      } catch (err) {
        console.error('Failed to check active subscription:', err);
      }
    }
    checkActiveSubscription();
    return () => { isMounted = false; };
  }, [activeWorkspace.id]);

  useEffect(() => {
    let isMounted = true;
    async function fetchClientSecret() {
      setLoading(true);
      setError(null);
      try {
        const res = await sendRequest({
          method: 'post',
          url: API.WORKSPACE.subscription(activeWorkspace.id),
          data: {
            price: plan.price * editableMembersCount,
            membership,
            subscriptionMode,
          },
        });
        if (isMounted) setClientSecret(res.data?.client_secret);
      } catch (err) {
        if (isMounted) setError('Unable to initialize payment. Please try again later.');
      }
      if (isMounted) setLoading(false);
    }
    fetchClientSecret();
    return () => { isMounted = false; };
    // Only fetch client secret once on mount - member count changes don't require new payment intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCouponApplied = (couponData) => {
    setDiscountInfo(couponData);
  };

  const options = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'stripe',
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
      },
    },
  } : null;

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
    >
      <Box
        id="PaymentModal"
        sx={{
          width: 1065,
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 4,
          background: '#fff',
          borderRadius: 2,
        }}
      >
        <h5 className="mb-2 text-600">
          Checkout
        </h5>
        <p className="mb-3">Add a card below and proceed to payment</p>
        {/* Debug info - remove after testing */}
        {hasActiveSubscription && (
          <div style={{
            backgroundColor: '#E3F2FD',
            padding: '8px',
            marginBottom: '12px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
          >
            ℹ️ Active subscription detected - mode selection available below
          </div>
        )}
        <div className="d-flex gap-3">
          <div className="w-50">
            <Card>
              <CardHeader>
                <img src={CardImage} alt="Order" />
                <h6 className="fw-semibold pl-2">Payment Method</h6>
              </CardHeader>
              <CardBody>
                {loading && <div>Loading payment form...</div>}
                {error && <div className="text-danger">{error}</div>}
                {!loading && !error && clientSecret && (
                  <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm
                      price={plan.price * editableMembersCount}
                      membership={membership}
                      onPaymentSuccess={onPaymentSuccess}
                      onCouponApplied={handleCouponApplied}
                      subscriptionMode={subscriptionMode}
                    />
                  </Elements>
                )}
              </CardBody>
            </Card>
          </div>
          <div className="w-50">
            <Card>
              <CardHeader>
                <img src={OrderImage} alt="Order" />
                <h6 className="fw-semibold pl-2">Order Summary</h6>
              </CardHeader>
              <CardBody>
                <h6 className="fw-semibold size-xs py-2 px-3 mb-2">Subscription Plan</h6>
                <div className="px-3 d-flex justify-content-between">
                  <span>{`${plan.plan} Plan`}</span>
                  <span>{`$${plan.price}`}</span>
                </div>
                <div className="px-3 d-flex justify-content-between align-items-center mb-2">
                  <span>Members</span>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditableMembersCount(Math.max(minimumMembers, editableMembersCount - 1))}
                      disabled={editableMembersCount <= minimumMembers}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: editableMembersCount <= minimumMembers ? '#f5f5f5' : '#fff',
                        cursor: editableMembersCount <= minimumMembers ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={editableMembersCount}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        // eslint-disable-next-line no-restricted-globals
                        if (!isNaN(value) && value >= minimumMembers && value <= 1000) {
                          setEditableMembersCount(value);
                        }
                      }}
                      min={minimumMembers}
                      max="1000"
                      style={{
                        width: '60px',
                        textAlign: 'center',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '14px',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setEditableMembersCount(Math.min(1000, editableMembersCount + 1))}
                      disabled={editableMembersCount >= 1000}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: editableMembersCount >= 1000 ? '#f5f5f5' : '#fff',
                        cursor: editableMembersCount >= 1000 ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="px-3 mb-2" style={{ fontSize: '12px', color: '#666' }}>
                  <span>
                    Minimum:
                    {minimumMembers}
                    {' '}
                    members
                  </span>
                </div>

                {hasActiveSubscription && (
                  <>
                    <hr />
                    <h6 className="fw-semibold size-xs py-2 px-3 mb-2">Subscription Mode</h6>
                    <div className="px-3 mb-2">
                      <div className="d-flex flex-column gap-2">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '12px',
                            border: subscriptionMode === 'override' ? '2px solid #53A551' : '1px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: subscriptionMode === 'override' ? '#F0F7FF' : '#fff',
                          }}
                        >
                          <input
                            type="radio"
                            name="subscriptionMode"
                            value="override"
                            checked={subscriptionMode === 'override'}
                            onChange={(e) => setSubscriptionMode(e.target.value)}
                            style={{ marginTop: '2px', marginRight: '8px' }}
                          />
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                              Override Current Subscription
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>
                              Deactivate your current subscription and activate the new one instantly
                            </div>
                          </div>
                        </label>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '12px',
                            border: subscriptionMode === 'extend' ? '2px solid #53A551' : '1px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: subscriptionMode === 'extend' ? '#F0F7FF' : '#fff',
                          }}
                        >
                          <input
                            type="radio"
                            name="subscriptionMode"
                            value="extend"
                            checked={subscriptionMode === 'extend'}
                            onChange={(e) => setSubscriptionMode(e.target.value)}
                            style={{ marginTop: '2px', marginRight: '8px' }}
                          />
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                              Schedule After Current Subscription
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>
                              Keep your current subscription active and schedule this plan to start when it expires
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <hr />
                <div className="px-3 d-flex justify-content-between mb-2">
                  <span className="fw-medium">Subtotal</span>
                  <span className={discountInfo ? 'text-decoration-line-through' : 'fw-medium'}>
                    {`$${originalPrice.toFixed(2)}`}
                  </span>
                </div>
                {discountInfo && (
                  <>
                    <div className="px-3 d-flex justify-content-between mb-2" style={{ color: '#2E7D32' }}>
                      <span className="fw-medium">
                        Discount (
                        {discountInfo.coupon.code}
                        )
                      </span>
                      <span className="fw-medium">
                        -$
                        {discountInfo.discount.toFixed(2)}
                      </span>
                    </div>
                    <hr />
                  </>
                )}
                <div className="px-3 d-flex justify-content-between">
                  <span className="fw-bold">Total</span>
                  <span className="fw-bold" style={{ color: discountInfo ? '#2E7D32' : 'inherit' }}>
                    {`$${finalPrice.toFixed(2)}`}
                  </span>
                </div>
                {discountInfo && (
                  <div className="px-3 mt-2">
                    <div
                      className="size-xs p-2"
                      style={{ backgroundColor: '#E8F5E9', borderRadius: '4px', color: '#2E7D32' }}
                    >
                      🎉 You saved $
                      {discountInfo.discount.toFixed(2)}
                      !
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

PaymentModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  activeWorkspace: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  membership: PropTypes.number.isRequired,
  membersCount: PropTypes.number.isRequired,
  plan: PropTypes.shape({
    plan: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    workspaceId: PropTypes.number.isRequired,
    Members: PropTypes.number,
  }).isRequired,
  onPaymentSuccess: PropTypes.func,
};

PaymentModal.defaultProps = {
  onPaymentSuccess: null,
};

export default PaymentModal;
